import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAdminOrgs } from '@/lib/hooks/useAdmin';
import { useMe } from '@/lib/hooks/useBootstrap';
import { cn } from '@/lib/utils';

export function AdminOrgsPage() {
  const { data: me } = useMe(true);
  const isAdmin = me?.user.is_admin === true;
  const [search, setSearch] = useState('');
  const { data } = useAdminOrgs(search);

  if (me && !isAdmin) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center text-sm text-slate-500">
        This area is restricted to Momentum AI staff.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-8 py-10">
      <header className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">_admin · orgs</h1>
        <p className="mt-1 text-sm text-slate-500">
          {data ? `${data.total} total` : 'loading…'}
        </p>
      </header>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Search</CardTitle>
          <CardDescription>By name or slug.</CardDescription>
        </CardHeader>
        <Input
          placeholder="Type to filter…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Card>

      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900">
            <tr>
              <th className="px-3 py-2 font-medium">Org</th>
              <th className="px-3 py-2 font-medium">Tier</th>
              <th className="px-3 py-2 font-medium">Members</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {data?.items.map((o) => (
              <tr key={o.id} className={cn(o.suspended && 'opacity-60')}>
                <td className="px-3 py-2">
                  <Link to="/_admin/orgs/$orgId" params={{ orgId: o.id }} className="font-medium hover:underline">
                    {o.name}
                  </Link>
                  <div className="font-mono text-[11px] text-slate-500">{o.slug}</div>
                </td>
                <td className="px-3 py-2 uppercase text-xs">{o.tier}</td>
                <td className="px-3 py-2">{o.members}</td>
                <td className="px-3 py-2 text-xs">
                  {o.suspended ? (
                    <span className="rounded bg-red-100 px-2 py-0.5 text-red-900 dark:bg-red-950 dark:text-red-200">Suspended</span>
                  ) : (
                    <span className="rounded bg-emerald-100 px-2 py-0.5 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">{o.status}</span>
                  )}
                  {o.founding_customer && (
                    <span className="ml-1 rounded bg-amber-100 px-2 py-0.5 text-amber-900 dark:bg-amber-950 dark:text-amber-200">Founding</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
